import { combineEpics, ofType } from "redux-observable";
import {
    catchError,
    debounceTime,
    from,
    map,
    mergeMap,
    of,
    skipWhile,
    switchMap,
    withLatestFrom,
} from "rxjs";
import {
    fetchBubblepoint,
    setBopd,
    setBopdValues,
    setBubblePoint,
    setBwpd,
    setFluidViscosity,
    setGasCalValues,
    setGasProduction2,
    setGLR2,
    setGOR,
    setGOR2,
    setOilAPI2,
    setOilCut,
    setSpGrOil,
    setSpGrOil2,
    setWaterCutCombinedSpGr,
} from "../../../store/calculation/calculation-input/FluidSlice";
import { RootState } from "../../../store/ConfigureStore"; // Ensure this is the correct import
import {
    getBubblePoint,
    getBWPDvalue,
    getFluidViscosityChange,
    getGasCalData,
    getOilAPI,
    getSpGrOil,
    getWateCutOilCut,
    getWaterCutCombinedSpGr,
} from "../../../services/calculations/calculation-input/fluid.service";
import { fluidViscosityPayload } from "../../../store/model/calculations/calculation";

const fetchWaterCutChangeOilCut = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "fluidTab/setWaterCut"
        ),
        // debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input))
        ),
        switchMap(([actionData, tab3Input]) => {
            return from(getWateCutOilCut(+tab3Input?.waterCut)).pipe(
                map((response) => {
                    return setOilCut(Number(response));
                }),
                catchError((error) => {
                    return of(console.error(error));
                })
            );
        })
    );
};

const fetchCombinedSpGrEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            'fluidTab/setSelectedRegion',
            "fluidTab/setSpGrWater",
            "fluidTab/setSpGrOil",
            "fluidTab/setSpGrOil2",
            "fluidTab/setBwpd",
            "fluidTab/setBopd",
            "fluidTab/setGOR",
            "fluidTab/setGOR2",
            "fluidTab/setSpGrGas"
        ),
        // debounceTime(1000),
        withLatestFrom(
            state$?.pipe(
                map((state: RootState) => state?.wellInput?.tab2Input)
            ),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.region)),
            state$.pipe(map((state: RootState) => state?.fluid?.regionList))
        ),
        switchMap(
            ([
                action,
                tab2Input,
                fluidData,
                region,
                regionList,
            ]) => {
                const selectedRegion = regionList?.find((x: any) => x?.id === region);
                const BOPD = +fluidData?.bopd || 0;
                const BWPD = +fluidData?.bwpd || 0;
                const bottomHoleTemp = +fluidData?.bottomHoleTemp || 0;
                let GOR = +fluidData?.gor || 0;
                let gasCompression = +fluidData?.gasCompressibility || 0;
                const spGrGas = +fluidData?.spGrGas || 0;
                const spGrWater = +fluidData?.spGrWater || 0;
                const spGrOil = +fluidData?.spGrOil || 0;
                const oilAPI = +fluidData?.oilAPI || 0;
                const BOPDUnit = +fluidData?.unitBOPDListVal || 0;
                const BWPDUnit = +fluidData?.unitBWPDListVal || 0;
                const bottomHoleTempUnit = +fluidData?.unitBottomHoleTempListVal || 0;
                const gasAvailability = +fluidData?.gasAvailblty || 0;
                const GORUnit = +fluidData?.unitGORListVal || 0;
                const casingPressure =
                    tab2Input?.casingPressure ?? 0;
                const casingPressureUnit = +tab2Input?.unitCasingPressureListVal || 0;
                const flop = +tab2Input?.flop || 0;
                const bubblePressure = +fluidData?.bubblePoint || 0;
                const bubblePressureUnit = +fluidData?.unitBubblePointListVal || 0;
                const gasProduction = fluidData?.gasProduction || 0;
                const gasProductionUnit = +fluidData?.unitGasProductionListVal || 0;
                const flopUnit = +tab2Input?.unitFLOPListVal || 0;
                const regionName = selectedRegion?.name ?? "";
                if (gasAvailability === 0) {
                    GOR = 0;
                    gasCompression = 0;
                }
                const compositeSpGrInputs = {
                    SpGrOil: spGrOil,
                    SpGrWater: spGrWater,
                    OilCut: +fluidData?.oilCut || 0,
                    WaterCut: +fluidData?.waterCut || 0,
                    BWPD: BWPD,
                    BWPDUnit: BWPDUnit,
                    OilAPI: oilAPI,
                    BOPD: BOPD,
                    BOPDUnit: BOPDUnit,
                    SpGrGas: spGrGas,
                    GOR: GOR,
                    GORUnit: GORUnit,
                    BottomHoleTemp: bottomHoleTemp,
                    BottomHoleTempUnit: bottomHoleTempUnit,
                    bubblePointPressure: bubblePressure,
                    bubblePointPressureUnit: bubblePressureUnit,
                    FLOP: flop,
                    FLOPUnit: flopUnit,
                    CasingPressure: casingPressure,
                    CasingPressureUnit: casingPressureUnit,
                    IsGas: gasAvailability === 1 ? true : false,
                    GasCompressibility: gasCompression,
                    GasProduction: gasProduction,
                    GasProductionUnit: gasProductionUnit,
                    RegionName: regionName,
                };
                return from(getWaterCutCombinedSpGr(compositeSpGrInputs)).pipe(
                    map((response) => {
                        return setWaterCutCombinedSpGr(response);
                    }),
                    catchError((error) => {
                        return of(console.error(error));
                    })
                );
            }
        )
    );
};

const fetchBubblePoint = (actions$: any, state$: any) => {
    return actions$.pipe(
        ofType(
            "fluidTab/fetchBubblePoint",
            "fluidTab/setBottomHoleTemp",
            "fluidTab/setGOR",
            "fluidTab/setOilAPI",
            "fluidTab/setSpGrGas"
        ),
        // debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input))
        ),
        switchMap(([actionData, tab3Input]) => {
            const postData = {
                BHT: +tab3Input?.bottomHoleTemp || 0,
                BHTUnit: +tab3Input?.unitBottomHoleTempListVal || 0,
                GOR: +tab3Input?.gor || 0,
                GORUnit: +tab3Input?.unitGORListVal || 0,
                OilAPI: +tab3Input?.oilAPI || 0,
                SpGrGas: +tab3Input?.spGrGas || 0,
                BubblePointUnit: +tab3Input?.unitBubblePointListVal || 0,
            };
            return from(getBubblePoint(postData)).pipe(
                map((data) => {
                    if (tab3Input.spGrGas === 0) {
                        return setBubblePoint(0);
                    } else {
                        if (data && !tab3Input?.bubblePoint) {
                            return setBubblePoint(data);
                        }
                        return setBubblePoint(tab3Input?.bubblePoint);
                    }
                }),
                catchError((error) => {
                    return of(console.error(error));
                })
            );
        })
    );
};

const GetOilApiChange = (actions$: any, state$: any) => {
    return actions$.pipe(
        ofType("fluidTab/setOilAPI"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input))
        ),
        switchMap(([actionData, tab3Input]) => {
            const gasinputApi = +tab3Input.oilAPI || 0;
            return from(getOilAPI(gasinputApi)).pipe(
                map((data) => {
                    return setSpGrOil2(Number(data));
                }),
                catchError((error) => {
                    return of(console.error(error));
                })
            );
        })
    );
};

const GetFluidViscosity = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "fluidTab/fetchFluidViscosity",
            "fluidTab/setBottomHoleTemp",
            "fluidTab/setGOR",
            "fluidTab/setOilAPI",
        ),
        // debounceTime(2000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input))
        ),
        skipWhile(([actionData, tab3Input]) => {
            return actionData.type !== "fluidTab/fetchFluidViscosity" && (+tab3Input.fluidViscosity > 0 || +tab3Input.gor <= 0 || +tab3Input.oilAPI <= 0 || +tab3Input.bottomHoleTemp <= 0)
        }),
        switchMap(([actionData, tab3Input]) => {
            const GOR = tab3Input.gor || 0;
            const BottomHoleTemperature = +tab3Input.bottomHoleTemp || 0;
            const OilApi = +tab3Input.oilAPI || 0;
            const GORUnit = +tab3Input.unitGORListVal || 0;
            const BottomHoleTemperatureUnit = +tab3Input.unitBottomHoleTempListVal || 0;
            let ViscosityData = { GOR, BottomHoleTemperature, OilApi, GORUnit, BottomHoleTemperatureUnit } as fluidViscosityPayload;
            return from(getFluidViscosityChange(ViscosityData)).pipe(
                map((response) => {
                    if (response && String(response) === "Infinity") {
                        return setFluidViscosity("");
                    }
                    return setFluidViscosity(Number(response));
                }),
                catchError((error) => {
                    return of(console.error(error));
                })
            );
        })
    );
};

const GetSpGrOilChanges = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("fluidTab/setSpGrOil"),
        // debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input))
        ),
        switchMap(([actionData, tab3Input]) => {
            return from(getSpGrOil(+tab3Input?.spGrOil)).pipe(
                map((response) => {
                    return setOilAPI2(Number(response));
                }),
                catchError((error) => {
                    return of(console.error(error));
                })
            );
        })
    );
};

const gasProdChange = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "fluidTab/setWaterCut",
            "well/setDesiredProductionRate",
            "fluidTab/setGasAvailability",
        ),
        // debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input))
        ),
        switchMap(([actionData, tab2Input, tab3Input]) => {
            const postData = {
                BOPDUnit: +tab3Input.unitBOPDListVal || 0,
                BWPDUnit: +tab3Input.unitBWPDListVal || 0,
                ProductionRate: +tab2Input.productionRate || 0,
                ProductionRateUnit: +tab2Input.unitProductionRateListVal || 0,
                WaterCut: +tab3Input.waterCut || 0
            }
            return from(getBWPDvalue(postData)).pipe(
                mergeMap((response) => [
                    setBopdValues(response),
                    setBopd(Number(response?.bopd)),
                    setBwpd(Number(response?.bwpd))
                ]),
                catchError((error) => {
                    return of(console.error(error));
                })
            );
        })
    );
};

const gasCalDataGor = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("fluidTab/setGOR"),
        // debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input))),
        switchMap(([actionData, tab2Input, tab3Input]) => {
            let postData = {
                BOPD: +tab3Input.bopd || 0,
                inputval: +tab3Input.gor || 0,
                BOPDUnit: +tab3Input.unitBOPDListVal || 0,
                inputvalUnit: +tab3Input.unitGORListVal || 0,
                GLRUnit: +tab3Input.unitGLRListVal || 0,
                GORUnit: +tab3Input.unitGORListVal || 0,
                GasProductionUnit: +tab3Input.unitGasProductionListVal || 0,
                WaterCut: +tab3Input.waterCut || 0,
                Productionrate: +tab2Input.productionRate || 0,
                productionrateUnit: +tab2Input.unitProductionRateListVal || 0,
                type: 1
            }
            return from(getGasCalData(postData)).pipe(
                mergeMap((data) => [
                    setGasProduction2(Number(data?.gasProduction)),
                    setGLR2(Number(data?.glr)),
                ]),
                catchError((error) => {
                    return of(console.error(error));
                })
            )
        })
    );
};

const gasCalDataGasProduction = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("fluidTab/setGasProduction"),
        // debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input))
        ),
        switchMap(([actionData, tab2Input, tab3Input]) => {
            let postData = {
                BOPD: +tab3Input.bopd || 0,
                inputval: +tab3Input.gasProduction || 0,
                BOPDUnit: +tab3Input.unitBOPDListVal || 0,
                inputvalUnit: +tab3Input.unitGasProductionListVal || 0,
                GLRUnit: +tab3Input.unitGLRListVal || 0,
                GORUnit: +tab3Input.unitGORListVal || 0,
                GasProductionUnit: +tab3Input.unitGasProductionListVal || 0,
                WaterCut: +tab3Input.waterCut || 0,
                Productionrate: +tab2Input.productionRate || 0,
                productionrateUnit: +tab2Input.unitProductionRateListVal || 0,
                type: 0
            }

            return from(getGasCalData(postData)).pipe(
                mergeMap((data) => [
                    setGOR2(Number(data?.gor)),
                    setGLR2(Number(data?.glr)),
                    fetchBubblepoint(),
                ]),
                catchError((error) => {
                    return of(console.error(error));
                })
            )
        })
    )
};

const gasCalDataGlr = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("fluidTab/setGLR"),
        // debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input))
        ),
        switchMap(([actionData, tab2Input, tab3Input]) => {
            let postData = {
                BOPD: +tab3Input.bopd || 0,
                inputval: +tab3Input.glr || 0,
                BOPDUnit: +tab3Input.unitBOPDListVal || 0,
                inputvalUnit: +tab3Input.unitGLRListVal || 0,
                GLRUnit: +tab3Input.unitGLRListVal || 0,
                GORUnit: +tab3Input.unitGORListVal || 0,
                GasProductionUnit: +tab3Input.unitGasProductionListVal || 0,
                WaterCut: +tab3Input.waterCut || 0,
                Productionrate: +tab2Input.productionRate || 0,
                productionrateUnit: +tab2Input.unitProductionRateListVal || 0,
                type: 2
            }

            return from(getGasCalData(postData)).pipe(
                mergeMap((data) => [
                    setGasProduction2(Number(data?.gasProduction)),
                    setGOR2(Number(data?.gor)),
                    fetchBubblepoint(),
                ]),
                catchError((error) => {
                    return of(console.error(error));
                })
            )
        })
    );
};

export const fluidEpic = combineEpics(
    fetchCombinedSpGrEpic,
    fetchWaterCutChangeOilCut,
    fetchBubblePoint,
    GetOilApiChange,
    GetFluidViscosity,
    GetSpGrOilChanges,
    gasProdChange,
    gasCalDataGor,
    gasCalDataGasProduction,
    gasCalDataGlr,
);
